import React from "react";
import { Trash2, PlusCircle, Tag } from "react-feather";
import toast from "react-hot-toast";
import ProductPicker from "./ProductPicker";
import { useLazyGetLastPurchasePriceQuery } from "../stockApi";
import { useGetProductGroupsQuery } from "../../masters/products/productsApi";
import { UNIT_LABELS } from "../../masters/products/unitLabels";
import { DEFAULT_VAT_PERCENT } from "../../../components/common/vatDefaults";

// Reusable editable line-items table shared by every Stock document form
// (LPO, Stock-In, Stock-Out, Return, Adjustment). Each document only turns
// on the columns it actually has (see `columns` prop) - LPO is the only one
// with VAT, Adjustment is the only one with no rate/amount at all (qty-only
// per Batch42_StockManagement.sql, no monetary value on that document).
//
// A row's product can be locked (picked/prefilled elsewhere - an LPO line
// being received into Stock-In, a Stock-Out line being returned, a Vehicle
// Request spare item) so the operator can't accidentally swap the product
// out from under a cross-document reference.
export const emptyRow = () => ({
  key: `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  lineType: "PRODUCT",
  productCode: "",
  productName: "",
  unit: "",
  qty: "",
  rate: "",
  vatPercent: "",
  remarks: "",
  reason: "",
  maxQty: null,
  locked: false,
  refId: null,
  // Batch185. On a quotation's free-text row: the optional group tag the user
  // picks. On an LPO row: the picked product's own group (read-only, used by the
  // quotation group check). Branch-local id - only meaningful with the form's branch.
  productGroupId: "",
  productGroupName: "",
  // LPO row seeded from a quotation group line: the product search is narrowed
  // to this group until a product is picked.
  pickFromGroupId: null,
});

// A quotation line the user entered as "Product group": free text underneath,
// with a group on it - either just chosen (isGroupLine) or loaded that way.
const isGroupRow = (row) => row.lineType === "FREETEXT" && Boolean(row.isGroupLine || row.productGroupId);

// Half away from zero to the cent, same as the API's StockVat.Round2 and SQL
// ROUND - EPSILON so 1.005 doesn't become 1.00 through float error.
export const round2 = (v) => Math.sign(v) * Math.round((Math.abs(v) + Number.EPSILON) * 100) / 100;

// columns.roundPerLine (Stock-In/Stock-Out, 2026-09-14): amount and VAT are
// rounded per line and totals are sums of the rounded figures - exactly how
// the API stores them (Batch172), so the form, the saved document and its
// print all show the same cents. LPO/Quotation keep their unrounded math.
export const computeLineAmount = (row, columns) => {
  const qty = Number(row.qty) || 0;
  const rate = columns.showRate ? Number(row.rate) || 0 : 0;
  const r = columns.roundPerLine ? round2 : (v) => v;
  const base = r(qty * rate);
  const vatAmount = columns.showVat ? r((base * (Number(row.vatPercent) || 0)) / 100) : 0;
  return { base, vatAmount, lineTotal: base + vatAmount };
};

export const computeTotals = (rows, columns) =>
  rows.reduce(
    (acc, row) => {
      const { base, vatAmount, lineTotal } = computeLineAmount(row, columns);
      return {
        totalQty: acc.totalQty + (Number(row.qty) || 0),
        subTotal: acc.subTotal + base,
        vatAmount: acc.vatAmount + vatAmount,
        netAmount: acc.netAmount + lineTotal,
      };
    },
    { totalQty: 0, subTotal: 0, vatAmount: 0, netAmount: 0 }
  );

// One-click "fill from last purchase price" - GET /stock/last-purchase-price
// (Stock.ViewBalance) returns the most recent non-voided Stock-In's rate for
// this product, or all-nulls if it was never purchased (never an error).
// Used on Stock-Out only, where the issue price commonly mirrors what was
// last paid - not shown on documents that already have their own rate
// source (LPO lines are typed fresh, Stock-In prefills from the LPO itself).
const LastPurchasePriceButton = ({ branchId, productCode, onFill }) => {
  const [trigger, { isFetching }] = useLazyGetLastPurchasePriceQuery();

  const handleClick = async () => {
    const { data, error } = await trigger({ branchId, productCode });
    if (error || !data) return toast.error("Could not look up the last purchase price");
    if (data.unitRate == null) return toast.error("No prior purchase found for this product");
    onFill(data.unitRate);
    const date = data.stockInDate ? new Date(data.stockInDate).toLocaleDateString() : "";
    toast.success(
      `Last purchased at ${Number(data.unitRate).toFixed(2)} on ${date} (${data.stockInNo}, ${data.supplierName || "supplier unknown"})`
    );
  };

  return (
    <button
      type="button"
      className="btn btn-link p-0 mt-1 d-inline-flex align-items-center gap-1 small"
      onClick={handleClick}
      disabled={isFetching}
      title="Fill from last purchase price"
    >
      <Tag size={12} /> Last price
    </button>
  );
};

const LineItemsEditor = ({
  branchId,
  rows,
  onChange,
  columns = { showRate: true, showVat: false, showRemarks: false, showReason: false, showMaxQty: false, showLastPurchasePrice: false },
  disabled = false,
  // VAT % a newly added line starts with (VAT-showing callers only). 16% unless
  // the caller passes its own - LPO passes its header "Default VAT %".
  defaultVatPercent = DEFAULT_VAT_PERCENT,
  // Off by default so every existing Stock/LPO caller (always product-only
  // lines) is unaffected - Quotation Prototype/Quotation lines are the only
  // callers that turn this on, to also allow a plain-text (no product link)
  // line. computeLineAmount/computeTotals need no changes either way - they
  // only ever look at qty/rate/vatPercent, which both line types carry.
  allowFreeText = false,
  // Which ProductListItemRecord field auto-fills the Rate column on pick -
  // "purchaseRate" (what this branch pays a supplier, the LPO/Stock default)
  // or "salesRate" (what this branch charges a customer, used by Quotation
  // Prototype/Quotation - a customer-facing document has no business
  // defaulting to the purchase cost).
  rateField = "purchaseRate",
  // Batch185 - { [row.key]: { tone: "danger" | "warning", text } }. The LPO form
  // passes the result of its quotation group check; the editor only paints it.
  rowFlags = null,
}) => {
  // The group tag is a quotation-line concept, and allowFreeText is what marks a
  // quotation caller - so the list is only ever fetched there. The endpoint
  // returns every branch's groups; a group id is only valid inside its own branch.
  const { data: allGroups } = useGetProductGroupsQuery(undefined, { skip: !allowFreeText });
  const groupOptions = (allGroups || [])
    .filter((g) => branchId != null && Number(g.branchId) === Number(branchId))
    .sort((a, b) => String(a.groupName || "").localeCompare(String(b.groupName || "")));

  const updateRow = (key, patch) =>
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () =>
    onChange([...rows, { ...emptyRow(), vatPercent: columns.showVat ? String(defaultVatPercent) : "" }]);
  // A line's VAT still counts as "not chosen yet" while it is blank or still
  // the untouched default - picking a product then brings that product's own
  // Product Master VAT (0 for an exempt item). A rate the user typed stays.
  const isUntouchedVat = (value) => value === "" || value == null || Number(value) === Number(defaultVatPercent);
  const removeRow = (key) => onChange(rows.filter((r) => r.key !== key));

  return (
    <div className="table-responsive">
      {/* Explicit minWidth so the browser's auto table layout can never
          shrink inputs/selects below a legible size when this component
          renders inside a narrower container (e.g. the master-detail split
          view's detail pane, much narrower than LPO's full-page form) - the
          `<th width>` hints below are otherwise just suggestions under auto
          layout and get silently squashed. table-responsive's overflow-x
          then kicks in and scrolls horizontally instead of squeezing. */}
      <table className="table table-bordered align-middle" style={{ minWidth: 1000 }}>
        <thead>
          <tr>
            <th style={{ width: 40 }}>#</th>
            {allowFreeText && <th style={{ width: 110 }}>Type</th>}
            <th style={{ minWidth: 220 }}>{allowFreeText ? "Product / Description" : "Product"}</th>
            <th style={{ width: 90 }}>Unit</th>
            <th style={{ width: 110 }}>Qty{columns.showMaxQty ? " (max)" : ""}</th>
            {columns.showRate && <th style={{ width: 120 }}>Rate</th>}
            {/* With showLineTotal (Stock-In/Out) the columns read as the sum they
                are: Rate, Amount = Qty x Rate, VAT %, VAT, Total = Amount + VAT. */}
            {columns.showLineTotal && <th style={{ width: 120 }}>Amount (ex VAT)</th>}
            {columns.showVat && <th style={{ width: 90 }}>VAT %</th>}
            {columns.showVat && <th style={{ width: 110 }}>VAT Amt</th>}
            {columns.showRate && !columns.showLineTotal && <th style={{ width: 120 }}>Amount</th>}
            {columns.showLineTotal && <th style={{ width: 120 }}>Total (inc VAT)</th>}
            {columns.showRemarks && <th style={{ minWidth: 150 }}>Remarks</th>}
            {columns.showReason && <th style={{ minWidth: 150 }}>Reason</th>}
            {!disabled && <th style={{ width: 50 }} />}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => {
            const { base, vatAmount, lineTotal } = computeLineAmount(row, columns);
            const flag = rowFlags?.[row.key];
            return (
              <tr key={row.key} className={flag ? `table-${flag.tone}` : undefined}>
                <td>{idx + 1}</td>
                {allowFreeText && (
                  <td>
                    {/* "Product group" is a choice on screen only. It is SAVED as a
                        FREETEXT line carrying a productGroupId (Batch185) - the API has
                        no third line type, on purpose, so print and every report read it
                        as the free text it is. A saved line that carries a group comes
                        back showing as "Product group" for the same reason. */}
                    <select
                      className="form-control"
                      value={isGroupRow(row) ? "GROUP" : row.lineType || "PRODUCT"}
                      disabled={disabled}
                      onChange={(e) => {
                        const choice = e.target.value;
                        if (choice === "GROUP") {
                          updateRow(row.key, { lineType: "FREETEXT", isGroupLine: true, productCode: "", productName: "" });
                        } else if (choice === "FREETEXT") {
                          updateRow(row.key, { lineType: "FREETEXT", isGroupLine: false, productCode: "", productName: "", productGroupId: "", productGroupName: "" });
                        } else {
                          updateRow(row.key, { lineType: "PRODUCT", isGroupLine: false, productGroupId: "", productGroupName: "" });
                        }
                      }}
                    >
                      <option value="PRODUCT">Product</option>
                      {groupOptions.length > 0 && <option value="GROUP">Product group</option>}
                      <option value="FREETEXT">Free text</option>
                    </select>
                  </td>
                )}
                <td>
                  {row.locked ? (
                    <span>{row.productName}</span>
                  ) : allowFreeText && isGroupRow(row) ? (
                    <>
                      <select
                        className="form-control"
                        value={row.productGroupId || ""}
                        onChange={(e) => {
                          const group = groupOptions.find((g) => String(g.groupId) === e.target.value);
                          // The wording the customer reads starts as the group's name,
                          // and follows the group only while nobody has reworded it.
                          const untouched = !row.productName || row.productName === row.productGroupName;
                          updateRow(row.key, {
                            productGroupId: e.target.value,
                            productGroupName: group?.groupName || "",
                            productName: untouched ? group?.groupName || "" : row.productName,
                          });
                        }}
                        disabled={disabled}
                      >
                        <option value="">Select product group</option>
                        {groupOptions.map((g) => (
                          <option key={g.groupId} value={g.groupId}>{g.groupName}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        className="form-control form-control-sm mt-1"
                        placeholder="Wording for the customer (optional - group name if empty)"
                        value={row.productName || ""}
                        onChange={(e) => updateRow(row.key, { productName: e.target.value })}
                        disabled={disabled}
                        title="Printed on the quotation. The group itself is not printed - it is what the LPO is checked against."
                      />
                    </>
                  ) : allowFreeText && row.lineType === "FREETEXT" ? (
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Description"
                      value={row.productName || ""}
                      onChange={(e) => updateRow(row.key, { productName: e.target.value })}
                      disabled={disabled}
                    />
                  ) : (
                    <ProductPicker
                      branchId={branchId}
                      disabled={disabled}
                      groupId={!row.productCode ? row.pickFromGroupId : undefined}
                      value={row.productCode ? { value: row.productCode, label: row.productName } : null}
                      onChange={(opt) =>
                        updateRow(row.key, {
                          productCode: opt?.value ?? "",
                          productName: opt?.product?.productName ?? "",
                          productGroupId: opt?.product?.productGroupId ?? "",
                          productGroupName: opt?.product?.productGroupName ?? "",
                          unit: opt?.product?.unitLabel || row.unit,
                          rate: columns.showRate && !row.rate ? opt?.product?.[rateField] ?? row.rate : row.rate,
                          vatPercent:
                            columns.showVat && isUntouchedVat(row.vatPercent) && opt?.product?.vat != null
                              ? String(opt.product.vat)
                              : row.vatPercent,
                        })
                      }
                    />
                  )}
                  {flag?.text && (
                    <div className={`small fw-semibold mt-1 text-${flag.tone === "danger" ? "danger" : "dark"}`}>{flag.text}</div>
                  )}
                </td>
                <td>
                  <select
                    className="form-control"
                    value={row.unit || ""}
                    onChange={(e) => updateRow(row.key, { unit: e.target.value })}
                    disabled={disabled}
                  >
                    <option value="">--</option>
                    {UNIT_LABELS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input
                    type="number"
                    className="form-control"
                    value={row.qty}
                    onChange={(e) => updateRow(row.key, { qty: e.target.value })}
                    disabled={disabled}
                    max={row.maxQty ?? undefined}
                  />
                  {columns.showMaxQty && row.maxQty != null && (
                    <div className="text-muted small">of {row.maxQty}</div>
                  )}
                </td>
                {columns.showRate && (
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      value={row.rate}
                      onChange={(e) => updateRow(row.key, { rate: e.target.value })}
                      disabled={disabled}
                    />
                    {columns.showLastPurchasePrice && !disabled && row.productCode && branchId && (
                      <LastPurchasePriceButton
                        branchId={branchId}
                        productCode={row.productCode}
                        onFill={(unitRate) => updateRow(row.key, { rate: unitRate })}
                      />
                    )}
                  </td>
                )}
                {columns.showLineTotal && <td className="text-end">{base.toFixed(2)}</td>}
                {columns.showVat && (
                  <td>
                    <input
                      type="number"
                      className="form-control"
                      value={row.vatPercent}
                      onChange={(e) => updateRow(row.key, { vatPercent: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                )}
                {columns.showVat && <td className="text-end">{vatAmount.toFixed(2)}</td>}
                {/* Pre-VAT (qty x rate) by default, matching the LPO/Stock PDF's
                    "Amount" column and Sub Total below - not qty x rate + VAT,
                    which would show a different number here than the printed
                    document shows for the same line (VAT is its own adjacent
                    column). columns.amountIncludesVat flips this to the
                    VAT-inclusive line total instead, for documents (Quotation)
                    where "Amount" is meant to read as the customer's actual
                    per-line price, VAT already folded in. */}
                {columns.showRate && !columns.showLineTotal && <td className="text-end">{(columns.amountIncludesVat ? lineTotal : base).toFixed(2)}</td>}
                {/* Stock-In/Out (2026-09-14): the VAT-inclusive line total as its own
                    column, so Amount + VAT = Total reads across every line. */}
                {columns.showLineTotal && <td className="text-end fw-semibold">{lineTotal.toFixed(2)}</td>}
                {columns.showRemarks && (
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={row.remarks || ""}
                      onChange={(e) => updateRow(row.key, { remarks: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                )}
                {columns.showReason && (
                  <td>
                    <input
                      type="text"
                      className="form-control"
                      value={row.reason || ""}
                      onChange={(e) => updateRow(row.key, { reason: e.target.value })}
                      disabled={disabled}
                    />
                  </td>
                )}
                {!disabled && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-link text-danger p-1"
                      onClick={() => removeRow(row.key)}
                      title="Remove line"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
          {rows.length === 0 && (
            <tr>
              <td colSpan={10} className="text-center text-muted py-3">
                No lines added yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {!disabled && (
        <button type="button" className="btn btn-outline-primary btn-sm d-inline-flex align-items-center gap-1" onClick={addRow}>
          <PlusCircle size={14} /> Add Line
        </button>
      )}
    </div>
  );
};

export default LineItemsEditor;
